import { Modal } from 'antd'
import { ChangeEvent, useRef, useState } from 'react'
import useI18n from '@/hooks/useI18n'
import { SettingsIconSelect, SettingsIconSelected } from '@/icons'
import { paypalSubscribeCancel } from '@/apis/subscribe'
import { PaypalSubscribeCancelInfo } from '@/types/subscribe'
import { AppResp } from '@/types/api'
import LoadingImg from '@/icons/LoadingImg'
import useUserSubscription from '@/hooks/swr/useUserSubscription'

type Props = {
  open: boolean
  onClose?: () => void
  cancelFunc: (param?: PaypalSubscribeCancelInfo) => Promise<AppResp<undefined>>
  width?: number
}

const feedbackOptions = [
  "The features didn't meet my needs",
  'Unpleasant chat experience',
  "I don't use it often enough",
  "It's too expensive",
  'I found a better alternative',
  'I experienced technical issues',
  'Other (please specify below)',
]

const MAX_CHARS = 950

export default function UnsubscribeFeedbackModal({
  open,
  onClose,
  cancelFunc,
  width,
}: Props) {
  const [selectedOptions, setSelectedOptions] = useState<number[]>([])
  const [feedbackText, setFeedbackText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { t } = useI18n()
  const { mutate } = useUserSubscription()

  const handleFeedbackChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    if (text.length <= MAX_CHARS) {
      setFeedbackText(text)
    }
  }

  const handleSubmit = async () => {
    if (isSubmitting) return
    if (selectedOptions.length === 0 && !feedbackText.trim()) return
    setIsSubmitting(true)
    // reason_code为index+1
    // 有custom_content，则reason_code添加0
    const params: PaypalSubscribeCancelInfo = {
      reason_code: [
        ...selectedOptions
          .filter((item) => item < feedbackOptions.length - 1)
          .map((item) => item + 1),
      ].concat(feedbackText.trim() ? [0] : []),
    }

    if (feedbackText.trim()) {
      params.custom_content = feedbackText
    }

    await cancelFunc(params)
    setIsSubmitting(false)
    mutate()
    onClose?.()
  }

  return (
    <Modal
      open={open}
      className="p-0"
      styles={{
        content: {
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
          color: '#fff',
        },
      }}
      style={{
        paddingBottom: 0,
        top: '-5%',
      }}
      transitionName=""
      width={width || '100vw'}
      centered={true}
      footer={null}
      closeIcon={false}
      onCancel={onClose}
    >
      <div
        className="rounded-lg shadow-[0_-1px_2px_0_rgba(255,255,255,0.25)] backdrop-blur-[25px]"
        style={{
          background:
            'linear-gradient( 270deg, rgba(67,46,60,0.7) 0%, rgba(61,44,41,0.85) 58%, rgba(48,50,41,0.75) 98%)',
        }}
      >
        <div className="px-[15px] py-5">
          <h3 className="mb-3 text-center text-base font-medium text-white">
            {t("We're sorry to see you go")} 🥹💔
          </h3>
          <p className="mb-4 text-sm text-white/80">
            {t(
              "We'd really appreciate your feedback. Let us know why you're canceling—your input helps us improve and better serve you in the future."
            )}
          </p>

          <div className="max-h-[400px] overflow-y-auto">
            <div className="mb-4 rounded-lg bg-white/10 px-2">
              {feedbackOptions.map((option, index) => (
                <div
                  key={option}
                  className={`flex items-center justify-start gap-2 py-2 ${index !== feedbackOptions.length - 1 ? 'border-b border-white/5' : ''}`}
                >
                  <button
                    onClick={() => {
                      if (selectedOptions.includes(index)) {
                        setSelectedOptions(
                          selectedOptions.filter((item) => item !== index)
                        )
                      } else {
                        setSelectedOptions([...selectedOptions, index])
                      }
                    }}
                    className="flex h-5 w-5 items-center justify-center rounded-full"
                  >
                    {selectedOptions.includes(index) ? (
                      <SettingsIconSelected />
                    ) : (
                      <SettingsIconSelect />
                    )}
                  </button>
                  <div>
                    <div className="text-sm font-light text-white">
                      {t(option)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg bg-white/10 px-2 py-1">
              <textarea
                ref={textareaRef}
                placeholder={t(
                  "Have any thoughts or suggestions you'd like to share?"
                )}
                value={feedbackText}
                onChange={handleFeedbackChange}
                maxLength={MAX_CHARS}
                className="no-scrollbar min-h-[60px] w-full resize-none overflow-y-auto bg-white/0 text-sm text-white placeholder-white/30 focus:border-transparent focus:outline-none focus:ring-0"
                style={{ WebkitAppearance: 'none' }}
              />
            </div>
          </div>
        </div>

        <div className="flex h-[44px] border-t border-white/20 shadow-[0_2px_4px_0_rgba(255,255,255,0.05)]">
          <div
            onClick={onClose}
            className="flex flex-1 cursor-pointer items-center justify-center text-[15px] leading-[19px] text-white active:text-white/40"
          >
            {t('Not now')}
          </div>

          <div className="h-full w-px bg-white/20"></div>
          <div
            onClick={handleSubmit}
            className={`flex flex-1 cursor-pointer items-center justify-center text-[15px] leading-[19px] ${
              (selectedOptions.length === 0 && !feedbackText.trim()) ||
              isSubmitting
                ? 'text-white/30'
                : 'text-white'
            }`}
          >
            {t('Submit & Cancel')}
          </div>
        </div>
      </div>
      {isSubmitting && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingImg />
        </div>
      )}
    </Modal>
  )
}
